import React, { useMemo, useState } from 'react'
import { Button, Input, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import {
  Container,
  Grid,
  Paper,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer
} from "@mui/material"
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  makeCoreGetRequest,
  makeCorePostRequest,
  makeCorePutRequest
} from "../../../../../../../utils/api"
import { useNotification } from '../../../../../../../hooks'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { URLS } from '../../../../constants'
import { Box } from "@mui/system"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { formatPayloadDate } from "../../../../../../../utils/helpers"
import { serializeUnorderedReceptionProduct, UnorderedProductForm } from "../GoodsValidation/serializers"
import {
  getEmptyUnorderedForm,
  isAdditionalItem,
  isUnorderedScanResponse,
  normalizeProductCodeInput,
  toNumberOrNull
} from "../../../../helpers"

export type DSReceptionProductDTO = {
  id: number
  articleCode: number | null
  articleName: string | null
  eanCode: number | null
  scannedCode: string | null
  measureUnit: string | null
  receivedQuantity: number | null
  orderedQuantity: number | null
  difference: number | null
  expirationDate: string | null | [number, number, number]
  packagingUnit: number | null
  orderLineNumber: number | null
  unitsPerLogisticUnit: number | null
  pcbNumber: number | null
}

export type DSOrderDetailDTO = {
  dsReceptionProductDTOList: DSReceptionProductDTO[]
}

const toIsoDate = (d: DSReceptionProductDTO['expirationDate']): string => {
  if (!d) return ''
  if (Array.isArray(d) && d.length >= 3) {
    const [y, m, day] = d
    const mm = String(m).padStart(2, '0')
    const dd = String(day).padStart(2, '0')
    return `${y}-${mm}-${dd}`
  }
  return typeof d === 'string' ? d : ''
}

const ProductConfirmation: React.FC = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const queryClient = useQueryClient()

  const { id: orderId } = useParams<{ id: string }>()
  const [sp] = useSearchParams()
  const supplierCode = sp.get('supplierCode')

  const [edited, setEdited] = useState<Record<number, { qty: string; dlc: string }>>({})
  const [savingRow, setSavingRow] = useState<Record<number, boolean>>({})
  const [warningOpen, setWarningOpen] = useState(false)
  const [unscannedRows, setUnscannedRows] = useState<DSReceptionProductDTO[]>([])
  const [scanCode, setScanCode] = useState('')
  const [scanQty, setScanQty] = useState('')
  const [scanDlc, setScanDlc] = useState('')

  const [unorderedModalOpen, setUnorderedModalOpen] = useState(false)
  const [unorderedForm, setUnorderedForm] = useState<UnorderedProductForm>(getEmptyUnorderedForm())

  const [finalizeOpen, setFinalizeOpen] = useState(false)
  const [finalizeLoading, setFinalizeLoading] = useState(false)
  const [scanPreviewLoading, setScanPreviewLoading] = useState(false)

  const listKey = useMemo(() => ['receptionReviewList', orderId, supplierCode], [orderId, supplierCode])

  const { data, isLoading } = useQuery<DSOrderDetailDTO>(
    listKey,
    async () => {
      return await makeCoreGetRequest(`/shop-reception-mobile/ds-reception-product/product-list/${orderId}/${supplierCode}`, null)
    },
    {
      enabled: Boolean(orderId && supplierCode),
      onError: (err) => showNotification({ message: `Eroare la încărcarea produselor. ${formatErrorMessage(err)}`, type: 'error' })
    }
  )

  const rows = data?.dsReceptionProductDTOList ?? []

  const getDiffValue = (r: DSReceptionProductDTO): number => {
    if (typeof r.difference === 'number') return r.difference
    const rec = typeof r.receivedQuantity === 'number' ? r.receivedQuantity : 0
    const ord = typeof r.orderedQuantity === 'number' ? r.orderedQuantity : 0
    return rec - ord
  }

  const getRowGroup = (r: DSReceptionProductDTO): 0 | 1 | 2 => {
    const scanned = Boolean(r.scannedCode)
    if (!scanned) return 2

    const diff = getDiffValue(r)
    return diff !== 0 ? 0 : 1
  }

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ga = getRowGroup(a)
      const gb = getRowGroup(b)
      if (ga !== gb) return ga - gb

      const ola = a.orderLineNumber ?? Number.MAX_SAFE_INTEGER
      const olb = b.orderLineNumber ?? Number.MAX_SAFE_INTEGER
      if (ola !== olb) return ola - olb

      return a.id - b.id
    })
  }, [rows])

  const saveUnorderedEnabled = useMemo(() => {
    const receivedQuantity = toNumberOrNull(unorderedForm.receivedQuantity)
    const unitsPerLogisticUnit = toNumberOrNull(unorderedForm.unitsPerLogisticUnit)
    const pcbNumber = toNumberOrNull(unorderedForm.pcbNumber)

    return Boolean(
      unorderedForm.scannedCode.trim() &&
      unorderedForm.measureUnit.trim() &&
      unorderedForm.articleName.trim() &&
      receivedQuantity !== null && receivedQuantity > 0 &&
      unitsPerLogisticUnit !== null && unitsPerLogisticUnit > 0 &&
      pcbNumber !== null && pcbNumber > 0
    )
  }, [unorderedForm])

  type Ctx = { prev?: DSOrderDetailDTO; rowId?: number }

  const updateMutation = useMutation<void, unknown, { row: DSReceptionProductDTO; qty: number; dlc: string | null }, Ctx>({
    mutationFn: async ({ row, qty, dlc }) => {
      if (!row.scannedCode) return
      setSavingRow(m => ({ ...m, [row.id]: true }))
      return await makeCorePutRequest(
        `/shop-reception-mobile/ds-reception-product/reception/${orderId}/${supplierCode}/${row.scannedCode}?addQuantity=false`,
        { receivedQuantity: qty, pcbNumber: row.pcbNumber, expirationDate: dlc }
      )
    },
    onMutate: async ({ row, qty, dlc }) => {
      await queryClient.cancelQueries(listKey)
      const prev = queryClient.getQueryData<DSOrderDetailDTO>(listKey)
      queryClient.setQueryData<DSOrderDetailDTO>(listKey, (old) => {
        if (!old) return old
        return {
          ...old,
          dsReceptionProductDTOList: old.dsReceptionProductDTOList.map(p => p.id === row.id ? { ...p, receivedQuantity: qty, expirationDate: dlc } as DSReceptionProductDTO : p)
        }
      })
      return { prev, rowId: row.id }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(listKey, ctx.prev)
      showNotification({ message: `Nu s-a putut salva rândul. ${formatErrorMessage(err)}`, type: 'error' })
    },
    onSuccess: () => showNotification({ message: 'Produs salvat', type: 'success' }),
    onSettled: (_d, _e, _v, ctx) => {
      setSavingRow(m => {
        const { [ctx?.rowId as number]: _, ...rest } = m
        return rest
      })
      setEdited(m => {
        const { [ctx?.rowId as number]: __, ...rest } = m
        return rest
      })
      queryClient.invalidateQueries(listKey)
    }
  })

  const fetchScanPreview = async (scanned: string) => {
    return await makeCoreGetRequest(
      `/shop-reception-mobile/ds-reception-product/reception/${orderId}/${supplierCode}/${encodeURIComponent(scanned)}`,
      null
    )
  }

  const scanAddMutation = useMutation<void, unknown, { scanned: string; qty: number; dlc: string | null }>({
    mutationFn: async ({ scanned, qty, dlc }) => {
      return await makeCorePutRequest(
        `/shop-reception-mobile/ds-reception-product/reception/${orderId}/${supplierCode}/${encodeURIComponent(scanned)}?addQuantity=true`,
        { receivedQuantity: qty, expirationDate: dlc }
      )
    },
    onSuccess: () => {
      showNotification({ message: 'Produs adăugat prin scanare', type: 'success' })
      setScanCode('')
      setScanQty('')
      setScanDlc('')
      document.getElementById('scan-input')?.focus()
      queryClient.invalidateQueries(listKey)
    },
    onError: () => showNotification({ message: `Nu s-a putut adăuga produsul. Verifică dacă codul scanat este corect.`, type: 'error' })
  })

  const saveUnorderedMutation = useMutation(
    async () => {
      const body = serializeUnorderedReceptionProduct(unorderedForm)

      return await makeCorePostRequest(
        `/shop-reception-mobile/ds-reception-product/reception/unordered/${orderId}/${supplierCode}`,
        body
      )
    },
    {
      onSuccess: () => {
        showNotification({ message: 'Produs necomandat salvat.', type: 'success' })
        setUnorderedModalOpen(false)
        setUnorderedForm(getEmptyUnorderedForm())
        setScanCode('')
        setScanQty('')
        setScanDlc('')
        document.getElementById('scan-input')?.focus()
        queryClient.invalidateQueries(listKey)
      },
      onError: (error: any) => {
        showNotification({
          message: `Eroare la salvare produs necomandat. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const finalizeMutation = useMutation<void>({
    mutationFn: async () => {
      return await makeCorePostRequest(`/shop-reception-mobile/ds-order/finalize-reception/${orderId}/${supplierCode}`, null)
    },
    onSuccess: () => setFinalizeOpen(true),
    onError: (err) => showNotification({ message: `Finalizarea a eșuat. ${formatErrorMessage(err)}`, type: 'error' })
  })

  const finalizeSupportTraceability = useMutation<void>(
    async () => {
      return await makeCorePostRequest(
        `/shop-reception-mobile/ds-order/ds-support-traceability/finalize/${orderId}/${supplierCode}`,
        null
      )
    },
    {
      onSuccess: () => {
        navigate(generatePath(URLS.AUTH_SUPPLIERS_RECEIPT_REPORT, { id: orderId }) + `?supplierCode=${supplierCode}`)
      },
      onError: (err) =>
        showNotification({
          message: `Finalizarea a eșuat. ${formatErrorMessage(err)}`,
          type: 'error',
        }),
    }
  )

  const openFinalizeFlow = () => {
    setFinalizeLoading(true)
    finalizeMutation.mutate(undefined, {
      onSettled: () => setFinalizeLoading(false)
    })
  }

  const handleFinalizeClick = () => {
    const unscanned = (rows ?? []).filter(r => !r.scannedCode)
    if (unscanned.length > 0) {
      setUnscannedRows(unscanned)
      setWarningOpen(true)
      return
    }
    openFinalizeFlow()
  }

  const handleWarningContinue = () => {
    setWarningOpen(false)
    openFinalizeFlow()
  }

  const handleQtyChange = (row: DSReceptionProductDTO, val: string) => {
    setEdited(m => ({ ...m, [row.id]: { qty: val, dlc: m[row.id]?.dlc ?? toIsoDate(row.expirationDate) } }))
  }

  const handleDlcChange = (row: DSReceptionProductDTO, val: string) => {
    setEdited(m => ({ ...m, [row.id]: { qty: m[row.id]?.qty ?? String(row.receivedQuantity ?? ''), dlc: val } }))
  }

  const commitRow = (row: DSReceptionProductDTO) => {
    if (!row.scannedCode) return
    const raw = edited[row.id]?.qty ?? row.receivedQuantity
    const nextQty = raw === '' || raw === null || raw === undefined ? 0 : Number(raw)
    if (!Number.isFinite(nextQty) || nextQty < 0) {
      showNotification({ message: 'Cantitatea trebuie să fie ≥ 0', type: 'warning' })
      return
    }
    const nextDlc = (edited[row.id]?.dlc ?? toIsoDate(row.expirationDate)) || null
    updateMutation.mutate({ row, qty: nextQty, dlc: nextDlc })
  }

  const updateUnorderedField = (field: keyof UnorderedProductForm, value: string) => {
    setUnorderedForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleScanAdd = async () => {
    const scanned = scanCode.trim()
    const qty = toNumberOrNull(scanQty)

    if (!scanned) {
      showNotification({ message: 'Scanează un cod produs.', type: 'warning' })
      return
    }

    if (scanned.length > 13) {
      showNotification({ message: 'Codul produsului trebuie să aibă maximum 13 cifre.', type: 'warning' })
      return
    }

    if (qty === null || qty <= 0) {
      showNotification({ message: 'Cantitatea trebuie să fie > 0', type: 'warning' })
      return
    }

    try {
      setScanPreviewLoading(true)

      const product = await fetchScanPreview(scanned)

      if (isUnorderedScanResponse(product)) {
        setUnorderedForm({
          scannedCode: product?.scannedCode || scanned,
          measureUnit: '',
          receivedQuantity: scanQty,
          unitsPerLogisticUnit: '',
          pcbNumber: '',
          articleName: '',
          expirationDate: scanDlc || ''
        })
        setUnorderedModalOpen(true)
        return
      }

      scanAddMutation.mutate({ scanned, qty, dlc: scanDlc || null })
    } catch (error) {
      showNotification({
        message: 'Nu s-a putut verifica produsul scanat.',
        type: 'error'
      })
    } finally {
      setScanPreviewLoading(false)
    }
  }

  const handleCloseUnorderedModal = () => {
    setUnorderedModalOpen(false)
    setUnorderedForm(getEmptyUnorderedForm())
  }

  const handleSaveUnordered = () => {
    if (!saveUnorderedEnabled) {
      showNotification({ message: 'Completează toate câmpurile obligatorii pentru produsul necomandat.', type: 'warning' })
      return
    }

    saveUnorderedMutation.mutate()
  }

  const goPackageSelection = () => {
    setFinalizeOpen(false)
    navigate(generatePath(URLS.AUTH_SUPPLIERS_PACKAGE_INPUT, { id: orderId }) + `?supplierCode=${supplierCode}`)
  }

  const goEndReception = () => {
    setFinalizeOpen(false)
    finalizeSupportTraceability.mutate()
  }

  if (isLoading) return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <TitleHeader title="VALIDARE PRODUSE" />
          </Grid>

          <Grid item xs={12}>
            <Paper style={{ padding: 8 }}>
              <Box sx={{ width: '100%' }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  <Table
                    stickyHeader
                    sx={{
                      minWidth: 300,
                      tableLayout: 'fixed'
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontFamily: 'Ubuntu', color: '#f8f9fb', background: '#0970e6', width: '100%' }}><strong>Produs</strong></TableCell>
                        <TableCell align="right" style={{
                          fontFamily: 'Ubuntu',
                          color: '#f8f9fb',
                          background: '#0970e6',
                          width: '80%',
                          paddingLeft: 0,
                          paddingRight: 0,
                          textAlign: 'center'
                        }}><strong>Recepționat</strong></TableCell>
                        <TableCell style={{ fontFamily: 'Ubuntu', color: '#f8f9fb', background: '#0970e6', width: '100%' }}><strong>DLC</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" style={{ padding: 20 }}>
                            Nu există produse. Adaugă prin scanare mai jos.
                          </TableCell>
                        </TableRow>
                      )}

                      {sortedRows.map(row => {
                        const editable = !!row.scannedCode
                        const receivedVal = edited[row.id]?.qty ?? String(row.receivedQuantity ?? '')
                        const dlcVal = edited[row.id]?.dlc ?? toIsoDate(row.expirationDate)

                        const group = getRowGroup(row)
                        const additional = isAdditionalItem(row)

                        const rowBg =
                          additional ? '#fff3cd' :
                            group === 0 ? '#ffcccc' :
                              group === 1 ? '#ccffcc' :
                                undefined

                        return (
                          <TableRow
                            key={row.id}
                            sx={{
                              backgroundColor: rowBg,
                              '&:hover': { backgroundColor: rowBg }
                            }}
                          >
                            <TableCell style={{ whiteSpace: 'normal' }}>
                              {additional && (
                                <div style={{ fontWeight: 700, color: '#b26a00', marginBottom: 4 }}>
                                  Produs suplimentar
                                </div>
                              )}
                              <div><strong>Articol:</strong> {row.articleCode}</div>
                              <div><strong>EAN:</strong> {row.eanCode}</div>
                              <div><strong>Denumire:</strong> {row.articleName ?? '—'}</div>
                              <div>
                                <strong>Linie comandă:</strong>{' '}
                                {additional ? 'Produs suplimentar' : row.orderLineNumber}
                              </div>
                            </TableCell>
                            <TableCell align="right" style={{ width: 140 }}>
                              <Input
                                type="number"
                                variant="standard"
                                value={receivedVal}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQtyChange(row, (e.target as HTMLInputElement).value)}
                                onBlur={() => editable && commitRow(row)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && editable) (e.currentTarget as HTMLInputElement).blur() }}
                                disabled={!editable || !!savingRow[row.id]}
                              />
                            </TableCell>
                            <TableCell style={{ width: 190, paddingLeft: 0, paddingRight: 0 }}>
                              <DatePicker
                                label="DLC"
                                format="DD/MM/YYYY"
                                value={dlcVal ? dayjs(dlcVal) : null}
                                onChange={(newValue) => {
                                  if (!editable || !!savingRow[row.id]) return

                                  const iso = formatPayloadDate(newValue)
                                  handleDlcChange(row, iso)

                                  const nextDlc = iso || null
                                  const rawQty = edited[row.id]?.qty ?? row.receivedQuantity
                                  const nextQty = rawQty === '' || rawQty === null || rawQty === undefined ? 0 : Number(rawQty)

                                  if (!Number.isFinite(nextQty) || nextQty < 0) {
                                    showNotification({ message: 'Cantitatea trebuie să fie ≥ 0', type: 'warning' })
                                    return
                                  }

                                  updateMutation.mutate({ row, qty: nextQty, dlc: nextDlc })
                                }}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    disabled: !editable || !!savingRow[row.id]
                                  }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper style={{ padding: 12 }}>
              <Typography variant="medium" component="p" style={{ marginBottom: 8 }}>
                Adaugă produs prin scanare
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <Input
                    type="text"
                    variant="outlined"
                    label="Cod Produs"
                    value={scanCode}
                    onChange={e => {
                      const value = normalizeProductCodeInput((e.target as HTMLInputElement).value)
                      setScanCode(value)
                    }}
                    onKeyUp={event => {
                      if (event.which == 0) { handleScanAdd() }
                    }}
                    id="scan-input"
                    width="100%"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input
                    id="scanQtyInput"
                    type="number"
                    variant="standard"
                    label="Cantitate"
                    value={scanQty}
                    onChange={e => setScanQty((e.target as HTMLInputElement).value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleScanAdd() }}
                    width="100%"
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="DLC"
                    format="DD/MM/YYYY"
                    value={scanDlc ? dayjs(scanDlc) : null}
                    onChange={(newValue) => {
                      setScanDlc(formatPayloadDate(newValue))
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    label={
                      scanAddMutation.isLoading || scanPreviewLoading
                        ? 'Se adaugă…'
                        : 'Adaugă'
                    }
                    variant="contained"
                    onClick={handleScanAdd}
                    disabled={scanAddMutation.isLoading || scanPreviewLoading}
                    style={{ width: '100%' }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Button
              type="button"
              variant="contained"
              label={finalizeLoading ? 'Se finalizează…' : 'Finalizează'}
              onClick={handleFinalizeClick}
              disabled={finalizeLoading || finalizeMutation.isLoading}
              style={{ width: '100%' }}
            />
          </Grid>
        </Grid>

        <Dialog open={warningOpen} onClose={() => setWarningOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Atenție!</DialogTitle>
          <DialogContent>
            <Typography variant="medium" component="p" style={{ marginBottom: 12 }}>
              Următoarele produse nu au fost recepționate (nu au fost scanate):
            </Typography>

            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {unscannedRows.map(p => (
                <Box component="li" key={p.id} sx={{ mb: 0.5 }}>
                  <Typography variant="light" component="span">
                    {p.articleName ? p.articleName : 'Produs fără denumire'} — Articol: {p.articleCode}, EAN: {p.eanCode}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="light" component="p" style={{ marginTop: 12, opacity: 0.8 }}>
              Poți reveni pentru a corecta, sau poți continua finalizarea.
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              label="Înapoi"
              onClick={() => setWarningOpen(false)}
            />
            <Button
              variant="contained"
              label="Continuă"
              onClick={handleWarningContinue}
              disabled={finalizeLoading || finalizeMutation.isLoading}
            />
          </DialogActions>
        </Dialog>

        <Dialog open={unorderedModalOpen} onClose={handleCloseUnorderedModal} maxWidth="sm" fullWidth>
          <DialogTitle>Produs suplimentar / necomandat</DialogTitle>
          <DialogContent>
            <Typography variant="medium" component="p" style={{ marginBottom: 20 }}>
              Produsul scanat nu se regăsește în comanda inițială. Completează datele pentru înregistrare.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Input
                  type="text"
                  label="Cod scanat"
                  variant="standard"
                  width="100%"
                  value={unorderedForm.scannedCode}
                  disabled
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  type="text"
                  label="Denumire produs"
                  variant="standard"
                  width="100%"
                  value={unorderedForm.articleName}
                  onChange={e => updateUnorderedField('articleName', (e.target as HTMLInputElement).value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  type="select"
                  label="UM"
                  variant="standard"
                  width="100%"
                  value={unorderedForm.measureUnit}
                  values={[
                    { label: 'Buc', value: 'A' },
                    { label: 'Kg', value: 'KG' }
                  ]}
                  onChange={e => updateUnorderedField('measureUnit', (e.target as HTMLInputElement).value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  type="number"
                  label="Cantitate recepționată"
                  variant="standard"
                  width="100%"
                  value={unorderedForm.receivedQuantity}
                  onChange={e => updateUnorderedField('receivedQuantity', (e.target as HTMLInputElement).value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  type="number"
                  label="Produse per PCB"
                  variant="standard"
                  width="100%"
                  value={unorderedForm.unitsPerLogisticUnit}
                  onChange={e => updateUnorderedField('unitsPerLogisticUnit', (e.target as HTMLInputElement).value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  type="number"
                  label="Număr PCB"
                  variant="standard"
                  width="100%"
                  value={unorderedForm.pcbNumber}
                  onChange={e => updateUnorderedField('pcbNumber', (e.target as HTMLInputElement).value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <DatePicker
                  label="DLC"
                  format="DD/MM/YYYY"
                  value={unorderedForm.expirationDate ? dayjs(unorderedForm.expirationDate) : null}
                  onChange={(newValue) => {
                    updateUnorderedField('expirationDate', formatPayloadDate(newValue))
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              label="Renunță"
              onClick={handleCloseUnorderedModal}
              disabled={saveUnorderedMutation.isLoading}
            />
            <Button
              variant="contained"
              label={saveUnorderedMutation.isLoading ? 'Se salvează…' : 'Salvează'}
              onClick={handleSaveUnordered}
              disabled={!saveUnorderedEnabled || saveUnorderedMutation.isLoading}
            />
          </DialogActions>
        </Dialog>

        <Dialog open={finalizeOpen} onClose={() => setFinalizeOpen(false)}>
          <DialogTitle>Recepție finalizată</DialogTitle>
          <DialogContent>
            <Typography variant="medium" component="p">
              Doriți să continuați cu selecția de ambalaje?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" label="Selecție ambalaje" onClick={goPackageSelection} />
            <Button variant="contained" label="Încheie recepția" onClick={goEndReception} />
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  )
}

export default ProductConfirmation
